import { FailedTransaction, UserInfo } from "@/types/api/merchant/merchant.type";
import { toEnglishNumber, toPersianNumber } from "@/hooks/useThousandsSeparator";
import CustomButton from "@/shared/components/kit/button";
import CardKit from "@/shared/components/kit/card";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import moment from "jalali-moment";
import { Button, CircularProgress } from "@mui/material";
import AlertDialog from "@/shared/components/kit/modal";
import { useEffect, useRef, useState } from "react";
import { fetchFailedTransactions } from "@/api/merchant/request.get";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { useMutation } from "@tanstack/react-query";
import TableWallet from "@/modules/user/components/walletReports/table";
import { deleteFailedTransactions } from "@/api/merchant/request.delete";
import { formatPersianDate } from "@/hooks/persianToEnglishDigits";
/**

UserPersonalInformation
نمایش اطلاعات شخصی و حساب یک کاربر.

@param dataUser اطلاعات کاربر از نوع UserInfo
@returns کامپوننت React که اطلاعات کاربر را در قالب کارت و استک نمایش می‌دهد

شامل:
اطلاعات شخصی: نام، نام خانوادگی، کد ملی، شماره تلفن، تاریخ تولد، کد پستی، ایمیل، شغل و آدرس.
اطلاعات حساب: شماره کیف پول، موجودی نقدی و اعتباری، موجودی هدیه، تاریخ عضویت و انقضا، کد معرف.
دکمه‌های عملیاتی برای افزایش/کاهش اعتبار و هدیه کاربر.
کامپوننت به صورت ریسپانسیو طراحی شده و در نسخه موبایل به صورت ستون نمایش داده می‌شود.
*/


const UserPersonalInformation = ({ dataUser }: { dataUser: UserInfo | undefined }) => {
  const [isOpenModalFailedTransactions, setIsOpenModalFailedTransactions] = useState(false);
  const [cardNumber, setCardNumber] = useState<string>('');
  const [failedTxData, setFailedTxData] = useState<any[]>([]);
  const deletingIdRef = useRef<string | number | null>(null);

  const userInfoFields = [
    { label: "نام", value: dataUser?.first_name },
    { label: "نام خانوادگی", value: dataUser?.last_name },
    { label: "کدملی", value: toPersianNumber(`${dataUser?.nid}`) },
    { label: "نام پدر", value: "-" },
    { label: "شماره تلفن", value: toPersianNumber(`${dataUser?.phoneNumber}`) },
    { label: "تاریخ تولد", value: dataUser?.birthday },
    { label: "کد پستی", value: dataUser?.post_code },
    { label: "ایمیل", value: dataUser?.email },
    { label: "شغل", value: dataUser?.job },
    { label: "ادرس محل سکونت", value: dataUser?.address },
  ];

  const accountInfoFields = [
    { label: "شماره کیف پول", value: toPersianNumber(`${dataUser?.card_number}`) },
    { label: "موجودی نقدی", value: dataUser?.amount },
    { label: "موجودی اعتباری", value: dataUser?.credit },
    { label: "موجودی نقدی کاربر در فروشگاه (هدیه)", value: dataUser?.gift },
    { label: "موجودی اعتباری کاربر در فروشگاه", value: dataUser?.gift },
    { label: "تاریخ عضویت", value: dataUser?.created_at && moment(dataUser?.created_at).locale("fa").format("YYYY-MM-DD") },
    { label: "تاریخ انقضای اعتبار داخلی کاربر", value: dataUser?.expire },
    { label: "کد معرف کاربر", value: dataUser?.invited_ref_by },
  ];

  const tableHeaderConfig = [
    { id: "id", title: "شناسه" },
    { id: "nid", title: "کد ملی" },
    { id: "first_name", title: "نام" },
    { id: "last_name", title: "نام خانوادگی" },
    { id: "card_number", title: "شماره کارت" },
    { id: "price", title: "مبلغ" },
    { id: "error_message", title: "پیام خطا" },
    { id: "created_at", title: "زمان ساخت" },
    { id: "type", title: "نوع" },
    { id: "action", title: "حذف" }

  ];

  useEffect(() => {
    const wallet = accountInfoFields.find(item => item.label === "شماره کیف پول");
    setCardNumber(toEnglishNumber(wallet?.value ?? ''));
  }, [accountInfoFields]);

  const { mutate: fetchFailedTx, isPending} = useMutation({
    mutationFn: fetchFailedTransactions,
    onSuccess: (res) => {
      const formattedData = res.data.map((item:FailedTransaction) => ({
        ...item,
        created_at: new Date(item.created_at).toLocaleString('fa-IR')
      }));
      setFailedTxData(formattedData);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در گرفتن اطلاعات");
    },
  });

  const { mutate: deleteFailedTx, isPending: isDeleting } = useMutation({
    mutationFn: deleteFailedTransactions,
    onSuccess: () => {
      toast.success("تراکنش با موفقیت حذف شد");
      fetchFailedTx({cardNumber});
      setFailedTxData(prev => prev.filter(item => item.Transaction_id !== deletingIdRef.current));
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در حذف تراکنش");
    },
  });

  const handleOpenModalFailedTransactions = () => {
    fetchFailedTx({cardNumber});
    setIsOpenModalFailedTransactions(true);
  };

  return (
    <>
      <CardKit sx={{ bgcolor: "#2C2C2C", width: "100%" }} width={'100%'}>
        <StackKit sx={{ width: "100%" }} gap={2}>
          <StackKit direction={'row'} sx={{ width: "100%", justifyContent: 'space-between' }} >
            <TypoGraphyKit variant="h6" sx={{ textAlign: "right" }}>
              اطلاعات شخصی کاربر
            </TypoGraphyKit>
            <Button variant="contained" size="small" onClick={() => handleOpenModalFailedTransactions()}>
              تراکنش های ناموفق
            </Button>
          </StackKit>
          {userInfoFields.map((item) => (
            <StackKit key={item.label} direction={'row'} justifyContent="space-between" sx={{ width: "100%" }}>
              <TypoGraphyKit variant="body1">{item.label}</TypoGraphyKit>
              <TypoGraphyKit variant="body1">{item.value || "-"}</TypoGraphyKit>
            </StackKit>
          ))}

          <TypoGraphyKit variant="h6" sx={{ textAlign: "right" }}>
            اطلاعات حساب کاربر
          </TypoGraphyKit>

          {accountInfoFields.map(item => (
            <StackKit key={item.label} direction="row" justifyContent="space-between">
              <TypoGraphyKit>{item.label}</TypoGraphyKit>
              <TypoGraphyKit>{item.value || "-"}</TypoGraphyKit>
            </StackKit>
          ))}

          <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} sx={{ width: "100%" }}>
            <CustomButton sx={{ width: '100%' }}>
              <TypoGraphyKit variant="body1">افزایش / کاهش اعتبار کاربر</TypoGraphyKit>
            </CustomButton>
            <CustomButton sx={{ width: '100%' }}>
              <TypoGraphyKit variant="body1">افزایش / کاهش هدیه کاربر</TypoGraphyKit>
            </CustomButton>
          </StackKit>
        </StackKit>

      </CardKit>
      <AlertDialog
        agreeText="بستن"
        isHeader
        open={isOpenModalFailedTransactions}
        onClose={() => setIsOpenModalFailedTransactions(false)}
        header={<TypoGraphyKit variant="h6">تراکنش‌‌های ناموفق</TypoGraphyKit>}
        fullScreen={true}
      >
        {isPending ? (
          <StackKit sx={{ height: '100%', width: '100%' }} justifyContent="center" alignItems="center">
            <CircularProgress sx={{ color: "#fff" }} />
          </StackKit>
        ) : failedTxData.length > 0 ? (
          <TableWallet
            dataHeader={tableHeaderConfig}
            dataTbody={failedTxData}
            onDelete={(id) => {
              console.log(id)
              deletingIdRef.current = id;
              deleteFailedTx(id);
            }}
          />

        ) : (
          <TypoGraphyKit display={'flex'} justifyContent={'center'} alignItems={'center'} width={'100%'} height={'100%'} variant="body1">
            هیچ تراکنش ناموفقی برای این شماره کیف پول یافت نشد.
          </TypoGraphyKit>
        )}
      </AlertDialog>
    </>

  );
};

export default UserPersonalInformation;
function formatDateIfNeeded(created_at: string) {
  throw new Error("Function not implemented.");
}

